import React from 'react';

interface MissingImageIconProps {
  width?: number;
  height?: number;
  borderWidth?: number;
  fill?: string;
  className?: string;
  [key: string]: any; // Allow additional props for flexibility
}

// Generates a responsive SVG icon for missing images, with mountains and a sun/moon
// that adapts to the specified width and height, maintaining aspect ratio.
const MissingImageIcon: React.FC<MissingImageIconProps> = ({
  width = 200,
  height = 200,
  borderWidth = 4,
  fill = '#dddddd',
  className = '',
}) => {
  // Calculate the actual viewBox and element positions based on aspect ratio
  const aspectRatio = width / height;
  const isWide = aspectRatio > 1;

  // Base dimensions for calculations
  const baseSize = 512;

  // Calculate viewBox dimensions
  let viewBoxWidth, viewBoxHeight;
  if (isWide) {
    viewBoxWidth = baseSize;
    viewBoxHeight = baseSize / aspectRatio;
  } else {
    viewBoxWidth = baseSize * aspectRatio;
    viewBoxHeight = baseSize;
  }

  // Calculate border rectangle dimensions
  const borderX = borderWidth;
  const borderY = borderWidth;
  const borderRectWidth = viewBoxWidth - borderWidth * 2;
  const borderRectHeight = viewBoxHeight - borderWidth * 2;

  // Circle (sun/moon) position - keep it in upper left area
  const circleX = viewBoxWidth * 0.3;
  const circleY = viewBoxHeight * 0.25;
  const circleRadius = Math.min(viewBoxWidth, viewBoxHeight) * 0.09;

  // Mountain 1 (larger, background)
  const mountain1Points = [
    [viewBoxWidth * 0.15, viewBoxHeight - borderWidth],
    [viewBoxWidth * 0.45, viewBoxHeight * 0.4],
    [viewBoxWidth * 0.75, viewBoxHeight - borderWidth],
  ]
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

  // Mountain 2 (smaller, foreground)
  const mountain2Points = [
    [viewBoxWidth * 0.5, viewBoxHeight - borderWidth],
    [viewBoxWidth * 0.7, viewBoxHeight * 0.6],
    [viewBoxWidth * 0.9, viewBoxHeight - borderWidth],
  ]
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={width}
      height={height}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ maxWidth: '100%', height: 'auto' }}
    >
      {/* Border rectangle */}
      <rect
        x={borderX}
        y={borderY}
        width={borderRectWidth}
        height={borderRectHeight}
        fill="none"
        stroke={fill}
        strokeWidth={borderWidth}
        rx={borderWidth}
        ry={borderWidth}
      />

      {/* Sun/circle */}
      <circle cx={circleX} cy={circleY} r={circleRadius} fill={fill} />

      {/* Background mountain */}
      <polygon points={mountain1Points} fill={fill} opacity="0.7" />

      {/* Foreground mountain */}
      <polygon points={mountain2Points} fill={fill} />
    </svg>
  );
};

export default MissingImageIcon;
